import { AxiosError } from 'axios';
import { WorkplaceType } from '../../smallApps/jobs/types/index.types';

export type NullablePartial<T> = {
  [P in keyof T]?: T[P] | null;
};

export type JobPayload = NullablePartial<Job>;

export interface Education {
  academicDegree: string;
  major: string;
}

export type MatchingStatus =
  | 'MATCHING'
  | 'PAUSED_JOB_CAP'
  | 'PAUSED_GLOBAL_CEILING'
  | 'MATCHING_FAILED';

export interface Job {
  id: number;
  employerName: string;
  workplaceTypes: WorkplaceType[];
  externalId: string;
  title: string;
  altJobTitles: string[];
  recruiterName: string | null;
  hiringManagerName: string | null;
  hiringManagerId?: number;
  recruiterId?: number;
  location: {
    /** ISO alpha-2 of the country the job is performed in. Optional on write: blank leaves the stored value. */
    countryCode?: string | null;
    country: string;
    city: string;
    state: string;
    address: string;
    zipCode: string;
    latitude: number;
    longitude: number;
    radius: number;
  };
  educationList: Education[];
  certification: {
    certifications: string[];
    licensures: string[];
  };
  invitationSettings: {
    fitScore: number;
    updatedDays: number;
    distance: number;
  };
  description: string;
  salaryRange: string;
  minSalary: number;
  maxSalary: number;
  requiredSkills: string[];
  preferredSkills: string[];
  minTotalExperience: number;
  maxTotalExperience: number;
  minIndustryExperience: number;
  maxIndustryExperience: number;
  hasManagementExperience: boolean;
  status: string;
  customer: {
    id: number;
    name: string;
    logoUrl: string;
  };
  createdBy: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    customer: {
      id: number;
      name: string;
      logoUrl: string;
    };
    lastLoginAt: string;
    createdAt: string;
    updatedAt: string;
  };
  salaryCurrency: string;
  visaRequirements: string[];
  statusChangedAt: string;
  postedAt: number | string;
  /** Sent on create by a Super Admin, who belongs to no customer. */
  customerId?: number;
  customerIsSynthetic?: boolean;
  /**
   * avrio-backend's matching-limits state (2026-10-02). Absent on jobs saved
   * before matching limits existed, which render exactly as before.
   */
  matchingStatus?: MatchingStatus | null;
  /** Customer-facing, e.g. "Matching paused: monthly limit reached". */
  matchingStatusReason?: string | null;
  matchingStatusUpdatedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RioMessage {
  convoId: null | string;
  isFromBot: boolean;
  msgId: string;
  msgTime: number;
  personId: string;
  positionId: number;
  questionId: null | string;
  service: string;
  text: string;
}

export interface Candidate {
  /**
   * True when THIS viewer sees the candidate anonymised (avrio-backend
   * CandidateMaskingPolicy, the `masked` field on CandidateItem and Candidate):
   * `name` is a stable label ("Candidate 355YTV"), `email`, `phoneNumber`,
   * `employerName`, every experience's `employer` and every degree's
   * `institution` are absent, free text is scrubbed, and the resume, sharing
   * and inviting are refused by the server. The UI draws those parts as
   * hidden rather than as empty. Absent or false for everyone else.
   */
  masked?: boolean | null;
  employerName: string;
  fitScore: number;
  /**
   * Rio's interview-adjusted Fit Score, on the candidates LIST (avrio-backend
   * interviewAdjustedFitScore). Present only once an interview produced a usable
   * rescore; absent or null otherwise. SEPARATE from `fitScore`, the matcher's
   * score: the list's sort and its score filter run on `fitScore`, so this is
   * shown beside it as a marker and never in place of it.
   */
  interviewAdjustedFitScore?: number | null;
  id: number;
  jobTitle: string;
  lastActivityAt: number;
  name: string;
  status: string;
  email: string;
  phoneNumber: string;
  updatedAt: number;
  jobId: number;
  skills: string[];
  degrees: Array<{
    academicDegree: string;
    major: string;
    institution: string;
  }>;
  experiences: Array<{
    jobTitle: string;
    employer: string;
    id: number;
    startDate: number;
    endDate: number;
  }>;
  rioMessages: Array<RioMessage>;
  requiredSkills: Array<string>;
  preferredSkills: Array<string>;
  statusChangedAt: number;
  explanatoryNote?: string;
  /**
   * What the interview changed about the pre-interview Fit Score. Absent
   * means the candidate has not completed an interview — matches
   * avrio-backend's own openapi.yaml Rescore schema description verbatim;
   * `fitScore` above stays the resume-derived score either way, this is
   * additive, not a replacement.
   */
  rescore?: {
    scoreBefore: number;
    scoreAfter: number;
    delta: number;
    /** Dimensions the interview did not reach — still resume-only. */
    unresolved: string[];
    changes: Array<{
      dimension: string;
      before: number;
      after: number;
      delta: number;
      source: string;
      /** 0-1. How much the interview finding outweighed the resume prior. Only meaningful when source is "interview". */
      authority: number;
      explanation: string;
    }>;
  };
  dimensions: Record<
    string,
    {
      justification: string;
      score: string;
    }
  >;
}

export interface HiringManager {
  customerId: number;
  customerName: string;
  email: string;
  firstName: string;
  id: number;
  profilePictureUrl: null | string;
  lastName: string;
  role: string;
}

export interface CandidateFeedback {
  createdAt: number;
  hiringManagerName: string;
  profilePictureUrl: string | null;
  text: string;
}

export type InterviewSpeaker = 'RIO' | 'CANDIDATE' | 'UNKNOWN';

export interface InterviewMessage {
  msgId: string;
  speaker: InterviewSpeaker;
  body: string;
  /** Epoch seconds (avrio-backend writes an Instant as a number); read it with apiInstantToDate. */
  sentAt: number | string | null;
  questionId: string | null;
}

export interface InterviewConversation {
  convoId: string | null;
  jobId: string;
  /** Epoch seconds (avrio-backend writes an Instant as a number); read it with apiInstantToDate. */
  startedAt: number | string | null;
  messages: InterviewMessage[];
}

/** Matches Rio's own status wire values exactly (lower_snake_case) --
 * confirmed against InterviewResultsDynamoService.Status.toWireValue() in
 * avrio-backend, one convention across both services rather than a second
 * casing for the same states. `unknown` is a forward-compatibility escape
 * hatch on the backend's own read path, not a real interview state; callers
 * should treat it the same as `not_started`. */
export type InterviewStatus =
  | 'not_started'
  | 'in_progress'
  | 'completed'
  | 'unknown';

export interface DimensionObservation {
  /** 1-5, the same scale DimensionRow's dots already use -- confirmed
   * against avrio-rio's rescore.py, which documents its own observations
   * shape as `{name: {"score": 1-5, ...}}`. No conversion needed. */
  score: number;
  /** 0-1. */
  confidence: number;
  justification: string;
}

/**
 * The scoring version this page reads as its own: avrio-rio's
 * `agents/interview/rescore.py` `SCORING_VERSION`, the stamp Rio writes on
 * every completed interview (1 since 2026-09-05). Rio bumps it when the
 * scorer's composition changes, and a record carrying any other stamp was
 * scored by a different instrument, so its numbers are not shown next to the
 * resume-derived ones as if comparable. Bump this in the same release as Rio.
 */
export const SUPPORTED_SCORING_VERSION = 1;

export interface CandidateInterview {
  completed: boolean;
  /** Epoch seconds (avrio-backend writes an Instant as a number); read it with apiInstantToDate. */
  completedAt: number | string | null;
  transcript: {
    internalCandidateId: string;
    conversations: InterviewConversation[];
  };
  status: InterviewStatus;
  /** 0-100, the same scale as Candidate.fitScore. Null before the first
   * scored turn. */
  interviewScore: number | null;
  summary: string | null;
  /** Epoch seconds (avrio-backend writes an Instant as a number; read it
   * with apiInstantToDate). Stamped on every write (create/save-progress/
   * complete), so present even mid-interview -- the basis for the live/stalled
   * 5-minute threshold. */
  updatedAt: number | string | null;
  dimensionsAssessed: number | null;
  /** True when the interview ended before completion. */
  partial: boolean;
  /** Latest observation per dimension, keyed by the same dimension names
   * Candidate.dimensions uses -- no translation layer. Empty, not absent,
   * when nothing has been assessed yet. */
  observations: Record<string, DimensionObservation>;
  /**
   * LOAD-BEARING, per avrio-rio's own result payload comment: the stamp of
   * the scorer that produced interviewScore and the observations, written
   * when the interview completes -- so null while one is still running. The
   * page REFUSES rather than converts: a completed record whose stamp is not
   * SUPPORTED_SCORING_VERSION, or that has none (written before Rio stamped
   * versions, 2026-09-05), falls back to the resume score and shows neither a
   * delta nor the interview's dimension scores. A running interview has no
   * stamp yet and its live observations are the current scorer's.
   */
  scoringVersion: number | null;
  /**
   * The hiring manager's interview guide avrio-rio writes at completion
   * (Rio PRD 5.7): recommended questions, each mapped to a dimension or
   * required skill the interview left open, ordered by how much the gap
   * weighs in the fit score. Null before completion and on records written
   * before 20 Oct 2026.
   */
  interviewGuide?: InterviewGuide | null;
  /**
   * The candidate's own questions to Rio, with Rio's answer or the flag for
   * the recruiter (Rio PRD 5.2.3). Empty before completion and on older
   * records.
   */
  candidateQuestions?: CandidateQuestion[];
  /**
   * One passage of the candidate's own words for the candidate brief (Rio
   * PRD 5.7), chosen by avrio-rio at completion from an answer it scored as
   * clear evidence, never written by a model. Null before completion, on
   * records written before 23 Oct 2026, and when no answer was quotable. In
   * the anonymised view it is scrubbed, and an unknown area reads "other".
   */
  standoutQuote?: StandoutQuote | null;
  /**
   * Rio's AI-fluency reading (Work Fluency phase 1, avrio-rio#313): four
   * competencies judged from three questions on how the candidate uses AI at
   * work, each with the candidate's own words as evidence. Not part of the fit
   * score. Null or absent when the interview asked no AI questions.
   */
  aiFluency?: AiFluency | null;
  /**
   * The interview record avrio-rio keeps on the row (avrio-rio#254; avrio-backend#308): when the candidate started, when and why
   * the interview ended, and every integrity event with the server's time. Always present from the backend since #308 (empty
   * for a row written before #254); optional here so an older payload still reads.
   */
  record?: InterviewRecord | null;
  /**
   * How the candidate worked in Rio's work task (Work Fluency phase 2, avrio-rio `workspace`): one task read three ways, each with
   * a level and plain-language evidence, or a task left undone and why. Never scored, never part of the fit score. Null or absent
   * when the interview had no work task. In the anonymised view the candidate's own words (`quotes`) are withheld by the server.
   */
  workspace?: WorkReading | null;
  /**
   * True when Rio ended the interview with nothing scored (avrio-rio's `unscored` row flag): there is no `interviewScore`, and the
   * status is `completed` (it ran to its finish) or not (the candidate left the page twice; see `record.endedReason`). Absent on
   * an older payload; the page also reads "completed with no score" as unscored.
   */
  unscored?: boolean;
}

/** One integrity event Rio's chat service recorded. `at` is epoch seconds (read it with apiInstantToDate). */
export interface IntegrityEvent {
  /** `left`, `returned` or `paste_blocked`; a value this page has not seen reads as a plain "event". */
  event: string | null;
  at: number | string | null;
}

export interface InterviewRecord {
  /** When the candidate pressed Begin (the first turn). Epoch seconds. */
  startedAt?: number | string | null;
  lastActivityAt?: number | string | null;
  endedAt?: number | string | null;
  /** `plan_finished`, `candidate_finished` or `left_interview_twice`; absent while the interview is open. */
  endedReason?: string | null;
  leaveCount?: number | null;
  pasteBlockedCount?: number | null;
  integrityEvents?: IntegrityEvent[] | null;
}

/** A passage of the candidate's own words behind a reading. Empty in the anonymised view. */
export interface WorkQuote {
  quote: string;
  key: string | null;
}

export type WorkBuiltLevel =
  'all_pass' | 'some_fail' | 'not_run' | 'changed_after_run';
export type WorkDesignedLevel =
  'most_covered' | 'some_covered' | 'few_covered' | 'not_drawn';
export type WorkAiLevel =
  'careful' | 'mixed' | 'uncritical' | 'did_not_use' | 'guided';
export type WorkJudgementLevel = 'reasoned' | 'partial' | 'not_enough_evidence';
export type WorkSkipReason =
  'declined' | 'screen_not_shared' | 'unsupported' | 'declined_in_chat';

/** Built it (a code task): from the test runs the page reported. `evidence` is plain-language lines Rio's code wrote. */
export interface WorkBuilt {
  level: WorkBuiltLevel;
  evidence?: string[];
  passed?: number | null;
  failed?: number | null;
  runs?: number | null;
}

/** Designed it (a whiteboard or design task): how many of the task's checklist points the drawing covers. */
export interface WorkDesigned {
  level: WorkDesignedLevel;
  evidence?: string[];
  covered?: string[];
  missing?: string[];
  coveredCount?: number | null;
  total?: number | null;
}

export interface WorkUsedAi {
  level: WorkAiLevel;
  evidence?: string[];
  quotes?: WorkQuote[];
  /** Whether the assistant's planted mistake was caught, missed or never taken. */
  mistake?: 'caught' | 'missed' | 'not_given' | null;
  prompts?: { level: string; confidence: number | null } | null;
  /** A canvas task: the checklist point the assistant's design left out. */
  omission?: string | null;
}

export interface WorkJudgement {
  level: WorkJudgementLevel;
  evidence?: string[];
  quotes?: WorkQuote[];
}

export interface WorkGuided {
  maxLevel?: number | null;
  concepts?: {
    id: string;
    label: string | null;
    outcome: 'passing' | 'failing' | 'not_run' | 'no_tests';
    highestLevel?: number | null;
    requests?: number | null;
  }[];
  askedLevel?: string | null;
  /** Four notes in plain words, keyed by competency (description, discernment, delegation, diligence). */
  notes?: Record<string, string>;
  evidence?: string[];
}

export interface WorkReading {
  version?: number | null;
  /** The task's id, e.g. `dispatch-latency`. */
  taskId: string;
  /** `whiteboard` or `design` for a canvas task; absent for a code task. */
  kind?: 'whiteboard' | 'design' | null;
  language?: string | null;
  languageLabel?: string | null;
  builtIt?: WorkBuilt | null;
  designedIt?: WorkDesigned | null;
  usedAi?: WorkUsedAi | null;
  judgement?: WorkJudgement | null;
  guided?: WorkGuided | null;
  /** Set for a task left undone: why. The readings above are then all absent. */
  skipped?: WorkSkipReason | null;
  /** A skipped task: whether work had started before it was left. */
  afterWork?: boolean | null;
  /** A skipped task: how many times the tests were run before it was left. */
  runs?: number | null;
}

export interface StandoutQuote {
  quote: string;
  /** The dimension key the answer was scored in, as Candidate.dimensions keys them. */
  dimension: string;
  /** Rio's label for the dimension, worded for the candidate; this page names areas with dimensionLabel instead. */
  label?: string | null;
}

export type AiFluencyCompetency =
  | 'delegation'
  | 'description'
  | 'discernment'
  | 'diligence';

export type AiFluencyLevel =
  | 'aware'
  | 'practised'
  | 'fluent'
  | 'not_enough_evidence'
  | 'does_not_use_ai';

export interface AiFluencyEvidence {
  /** Verbatim from the candidate's answer. */
  quote: string;
  level: 'aware' | 'practised' | 'fluent' | 'uncertain';
  key: string | null;
}

export interface AiFluencyReading {
  level: AiFluencyLevel;
  evidence: AiFluencyEvidence[];
  count: number | null;
  confidence: number | null;
}

export interface AiFluency {
  version: number | null;
  /** False when the candidate said they don't use AI; null when unclear. */
  usesAi: boolean | null;
  competencies: Partial<Record<AiFluencyCompetency, AiFluencyReading>>;
}

export interface InterviewGuideQuestion {
  question: string;
  /** What a good answer contains. */
  listenFor: string | null;
}

export interface InterviewGuideDimension {
  dimension: string;
  label: string | null;
  /** `not_reached` (still rated from the CV) or `not_established` (asked, not established). */
  reason: string | null;
  why: string | null;
  /** The score the reason refers to, on the 1-5 scale. */
  score: number | null;
  questions: InterviewGuideQuestion[];
}

export interface InterviewGuideSkill {
  skill: string;
  /** `said_not`, `weak`, `claimed` or `not_covered`. */
  reason: string | null;
  why: string | null;
  question: string | null;
  listenFor: string | null;
}

export interface InterviewGuide {
  version: number | null;
  dimensions: InterviewGuideDimension[];
  skills: InterviewGuideSkill[];
  /** The first three questions, for a reader who reads nothing else. */
  top: string[];
  /** Set when nothing is open. */
  note: string | null;
}

export interface CandidateQuestion {
  question: string;
  answer: string | null;
  /** Rio did not have the detail and told the candidate the recruiter would follow up. */
  flagged: boolean;
  /** `interview` (asked mid-interview) or `candidate_questions` (the candidate's own turn). */
  phase: string | null;
  /** ISO-8601. */
  at: string | null;
}

export interface Context {
  isLoading: boolean;
  jobList: Array<Job>;
  totalJobElements: number;
  totalCandidateElements: number;
  totalCandidatePages: number;
  totalJobPages: number;
  totalSharedCandidatePages: number;
  totalSharedCandidateElements: number;
  error?: AxiosError;
  jobDetails: Job | null;
  candidates: Candidate[];
  sharedCandidates: Candidate[];
  hiringManagers: HiringManager[];
  candidate?: Candidate;
  // The candidate the in-flight GET_CANDIDATE was for; a reply for any other id is ignored.
  requestedCandidateId?: number;
  // Set when the load for this candidate id failed or answered for a different id.
  candidateLoadFailedFor?: number;
  candidateFeedback: CandidateFeedback | null;
  candidateDownloadCVUrl: string;
  autoPopulatedValues: Record<
    string,
    Array<string> | number | string | Record<string, unknown>
  >;
}

export enum Status {
  NEW = 'NEW',
  REVIEWED = 'REVIEWED',
  INVITATION_SENT = 'INVITATION_SENT',
  RIO_SCREENING = 'RIO_SCREENING',
  ALT_INTERVIEW = 'ALT_INTERVIEW',
  HM_REVIEW = 'HM_REVIEW',
  OFFERED = 'OFFERED',
  REJECTED = 'REJECTED',
  HIRED = 'HIRED',
  NOT_INTERESTED = 'NOT_INTERESTED',
}

export enum CandidateOfferEventType {
  OFFER_MADE = 'OFFER_MADE',
  OFFER_ACCEPTED = 'OFFER_ACCEPTED',
}

export enum CandidateOfferType {
  VERBAL = 'VERBAL',
  WRITTEN = 'WRITTEN',
}

/** Mirrors avrio-backend's CandidateOfferEvent (avrio-backend#127/#132/#134). */
export interface CandidateOfferEvent {
  id: number;
  jobId: number;
  candidateId: number;
  eventType: CandidateOfferEventType;
  offerType?: CandidateOfferType;
  compensationSummary?: string;
  proposedStartDate?: string;
  /** Server-recorded timestamp (ISO 8601) - when the backend received the event, not when the browser rendered it. */
  occurredAt: string;
  recordedByUserId: number;
  recordedByName: string;
  recordedByEmail: string;
  recordedByRole: string;
  notes?: string;
}

export enum State {
  INITIAL = 'idle',
  FETCH_JOB_LIST = 'FETCH_JOB_LIST',
  FETCH_JOB_BY_ID = 'FETCH_JOB_BY_ID',
  FETCH_JOB_AUTO_POPULATED_VALUES = 'FETCH_JOB_AUTO_POPULATED_VALUES',
  JOB_ADDED = 'JOB_ADDED',
  JOB_UPDATED = 'JOB_UPDATED',
  EMAIL_SENT = 'EMAIL_SENT',
  CREATE_JOB = 'CREATE_JOB',
  UPDATE_JOB = 'UPDATE_JOB',
  CANDIDATE_SHARED = 'CANDIDATE_SHARED',
  FETCH_CANDIDATES = 'FETCH_CANDIDATES',
  FETCH_SHARED_CANDIDATES = 'FETCH_SHARED_CANDIDATES',
  SEND_CANDIDATES = 'SEND_CANDIDATES',
  SHARE_CANDIDATE = 'SHARE_CANDIDATE',
  FETCH_HIRING_MANAGERS = 'FETCH_HIRING_MANAGERS',
  FETCH_JOB_HIRING_MANAGERS = 'FETCH_JOB_HIRING_MANAGERS',
  ASSIGN_HIRING_MANAGER = 'ASSIGN_HIRING_MANAGER',
  FETCH_RECRUITERS = 'FETCH_RECRUITERS',
  ASSIGN_RECRUITER = 'ASSIGN_RECRUITER',
  GET_CANDIDATE = 'GET_CANDIDATE',
  ADD_CANDIDATE_FEEDBACK = 'ADD_CANDIDATE_FEEDBACK',
  GET_CANDIDATE_FEEDBACK = 'GET_CANDIDATE_FEEDBACK',
  UPDATE_CANDIDATE_STATUS = 'UPDATE_CANDIDATE_STATUS',
  GET_CANDIDATE_DOWNLOAD_CV_URL = 'GET_CANDIDATE_DOWNLOAD_CV_URL',
  ERROR = 'error',
}

export enum EventType {
  FETCH_JOB_LIST = 'FETCH_JOB_LIST',
  FETCH_JOB_BY_ID = 'FETCH_JOB_BY_ID',
  FETCH_JOB_AUTO_POPULATED_VALUES = 'FETCH_JOB_AUTO_POPULATED_VALUES',
  RESET_JOB_AUTO_POPULATED_VALUES = 'RESET_JOB_AUTO_POPULATED_VALUES',
  CREATE_JOB = 'CREATE_JOB',
  UPDATE_JOB = 'UPDATE_JOB',
  FETCH_CANDIDATES = 'FETCH_CANDIDATES',
  FETCH_SHARED_CANDIDATES = 'FETCH_SHARED_CANDIDATES',
  SEND_CANDIDATES = 'SEND_CANDIDATES',
  SHARE_CANDIDATE = 'SHARE_CANDIDATE',
  FETCH_HIRING_MANAGERS = 'FETCH_HIRING_MANAGERS',
  FETCH_JOB_HIRING_MANAGERS = 'FETCH_JOB_HIRING_MANAGERS',
  ASSIGN_HIRING_MANAGER = 'ASSIGN_HIRING_MANAGER',
  FETCH_RECRUITERS = 'FETCH_RECRUITERS',
  ASSIGN_RECRUITER = 'ASSIGN_RECRUITER',
  GET_CANDIDATE = 'GET_CANDIDATE',
  ADD_CANDIDATE_FEEDBACK = 'ADD_CANDIDATE_FEEDBACK',
  GET_CANDIDATE_FEEDBACK = 'GET_CANDIDATE_FEEDBACK',
  UPDATE_CANDIDATE_STATUS = 'UPDATE_CANDIDATE_STATUS',
  GET_CANDIDATE_DOWNLOAD_CV_URL = 'GET_CANDIDATE_DOWNLOAD_CV_URL',
}

export type fetchJobAutopopulatedValuesEvent = {
  type: EventType.FETCH_JOB_AUTO_POPULATED_VALUES;
  payload: { title: string; description: string };
};

export type resetJobAutopopulatedValuesEvent = {
  type: EventType.RESET_JOB_AUTO_POPULATED_VALUES;
};

export type shareCandidateEvent = {
  type: EventType.SHARE_CANDIDATE;
  payload: { jobId: number; candidateId: number; userId: number };
};

export type getCandidateEvent = {
  type: EventType.GET_CANDIDATE;
  payload: { jobId: number; candidateId: number };
};

export type getCandidateDownloadCVUrlEvent = {
  type: EventType.GET_CANDIDATE_DOWNLOAD_CV_URL;
  payload: { jobId: number; candidateId: number };
};

export type updateCandidateStatusEvent = {
  type: EventType.UPDATE_CANDIDATE_STATUS;
  payload: { status: string; jobId: number; candidateId: number };
};


export type addFeedbackEvent = {
  type: EventType.ADD_CANDIDATE_FEEDBACK;
  payload: {
    jobId: number;
    candidateId: number;
    text: string;
    // Debrief fields (RIO-7). The endpoint persists text today; rating and
    // reason are sent so no frontend change is needed when the backend maps
    // them onto hmFeedbackRating and the structured reason. Jackson is
    // configured with FAIL_ON_UNKNOWN_PROPERTIES false, so they are ignored
    // rather than rejected until then.
    rating?: number;
    reason?: string;
  };
};

export type getFeedbackEvent = {
  type: EventType.GET_CANDIDATE_FEEDBACK;
  payload: { jobId: number; candidateId: number };
};

export type sendCandidatesEvent = {
  type: EventType.SEND_CANDIDATES;
  payload: {
    jobId: number;
    candidateIds: React.Key[];
    emails: string[];
    emailBody: string;
  };
};

export type fetchJobHiringmanagersEvent = {
  type: EventType.FETCH_JOB_HIRING_MANAGERS;
  payload: { jobId: number };
};

export type fetchHiringmanagersEvent = {
  type: EventType.FETCH_HIRING_MANAGERS;
};

export type assignHiringmanagersEvent = {
  type: EventType.ASSIGN_HIRING_MANAGER;
  payload: { jobId: number; hiringManagerId: number };
};

export type fetchRecruitersEvent = {
  type: EventType.FETCH_RECRUITERS;
};

export type assignRecruiterEvent = {
  type: EventType.ASSIGN_RECRUITER;
  payload: { jobId: number; recruiterId: number };
};

export type fetchJobListEvent = {
  type: EventType.FETCH_JOB_LIST;
  payload: {
    pageNumber: number;
    pageSize: number;
    sort?: string;
    order?: string;
    status?: string;
    /** Super Admin only: include jobs of customers flagged synthetic (test, E2E, demo). Off by default. */
    includeSynthetic?: boolean;
  };
};

export type fetchCandidatesEvent = {
  type: EventType.FETCH_CANDIDATES;
  payload: {
    jobId: number;
    pageNumber: number;
    pageSize: number;
    fitScore?: number;
    statuses?: Status[];
    hasMandatorySkills?: boolean;
    hasMandatoryTitleMatch?: boolean;
    maxDistance?: number;
    candidatesWithNoLocation?: boolean;
    isApplicant?: boolean;
    sort?: string;
    order?: string;
  };
};

export type fetchSharedCandidatesEvent = {
  type: EventType.FETCH_SHARED_CANDIDATES;
  payload: { pageNumber: number; pageSize: number };
};

export type createJobEvent = {
  type: EventType.CREATE_JOB;
  payload: { job: JobPayload };
};

export type fetchJobByIdEvent = {
  type: EventType.FETCH_JOB_BY_ID;
  payload: { id: number };
};

export type updateJobEvent = {
  type: EventType.UPDATE_JOB;
  payload: { id: number; job: JobPayload };
};

export type Event =
  | fetchJobListEvent
  | createJobEvent
  | fetchJobByIdEvent
  | updateJobEvent
  | fetchCandidatesEvent
  | sendCandidatesEvent
  | fetchHiringmanagersEvent
  | assignHiringmanagersEvent
  | fetchRecruitersEvent
  | assignRecruiterEvent
  | getCandidateEvent
  | addFeedbackEvent
  | getFeedbackEvent
  | fetchJobHiringmanagersEvent
  | fetchSharedCandidatesEvent
  | updateCandidateStatusEvent
  | getCandidateDownloadCVUrlEvent
  | shareCandidateEvent
  | fetchJobAutopopulatedValuesEvent
  | resetJobAutopopulatedValuesEvent;
